<template>
  <div class="card border-start border-4 border-success shadow-sm">
    <div class="card-header bg-light">
      <h6 class="m-0 fw-bold text-green">
        <font-awesome-icon :icon="['fas', 'key']" class="me-2" />
        Two-factor authentication (your account)
      </h6>
      <p class="small text-muted mb-0 mt-2">
        Reflects your institution’s policy and your own TOTP status. Institution-wide delivery (SMS vs authenticator) is configured under
        Institution settings.
      </p>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <b-spinner />
      </div>

      <template v-else-if="status">
        <b-alert v-if="loadError" show variant="warning">{{ loadError }}</b-alert>

        <dl class="row small mb-3">
          <dt class="col-sm-5">Institution requires 2FA at login</dt>
          <dd class="col-sm-7">{{ boolLabel(status.institution_two_factor_enabled) }}</dd>
          <dt class="col-sm-5">Institution delivery</dt>
          <dd class="col-sm-7 text-uppercase">{{ status.institution_two_factor_delivery_method || "—" }}</dd>
          <dt class="col-sm-5">OTP validity (minutes)</dt>
          <dd class="col-sm-7">{{ status.institution_two_factor_otp_validity_minutes ?? "—" }}</dd>
          <dt class="col-sm-5">Your 2FA enabled</dt>
          <dd class="col-sm-7">{{ boolLabel(status.user_two_factor_enabled) }}</dd>
          <dt class="col-sm-5">Pending confirmation</dt>
          <dd class="col-sm-7">{{ boolLabel(status.user_pending_confirmation) }}</dd>
        </dl>

        <!-- SMS -->
        <template v-if="isSmsDelivery">
          <b-alert show variant="info" class="akkurate-tfa-note">
            <strong>SMS delivery:</strong> Login codes are sent by SMS to your profile mobile number (<strong>phone_number</strong>
            on your user record).
            <span v-if="!phoneNumber"> You do not have a mobile number on file — add one under Overview so SMS can be used.</span>
            <span v-else> Current number on profile: <strong>{{ phoneNumber }}</strong>.</span>
          </b-alert>
          <p class="small text-muted mb-0">
            No authenticator app setup is used when the institution chooses SMS delivery.
          </p>
        </template>

        <!-- Authenticator (TOTP) -->
        <template v-else-if="isAuthenticatorDelivery && institution2faOn">
          <p class="small">
            Your institution uses an <strong>authenticator app</strong> (TOTP). After setup, use your app at login when prompted.
          </p>

          <!-- Start setup: backend requires current password -->
          <div v-if="!status.user_two_factor_enabled" class="mt-2">
            <b-button class="akkurate-green-btn me-2" :disabled="setupLoading" @click="openEnableModal">
              <b-spinner v-if="setupLoading" small class="me-1" />
              {{ status.user_pending_confirmation ? "Continue setup (new QR)" : "Set up authenticator" }}
            </b-button>
          </div>

          <b-alert v-if="setupError" show variant="danger" class="mt-3" dismissible @dismissed="setupError = ''">
            {{ setupError }}
          </b-alert>

          <div v-if="setupPayload" class="mt-3 p-3 border rounded bg-light">
            <p class="small fw-bold mb-2">Scan the QR in your authenticator app, or enter the key manually.</p>
            <div v-if="setupPayload.qr_code_svg" class="mb-2 tfa-qr-html" v-html="setupPayload.qr_code_svg" />
            <div v-if="setupPayload.setup_key" class="font-monospace small mb-0">
              Manual entry key: <strong>{{ setupPayload.setup_key }}</strong>
            </div>
            <p v-if="setupPayload.message" class="small text-muted mt-2 mb-0">{{ setupPayload.message }}</p>
          </div>

          <div v-if="showConfirmSection" class="mt-3">
            <label class="form-label small fw-bold" for="tfa-confirm">Enter 6-digit code from your app</label>
            <div class="d-flex flex-wrap align-items-end gap-2">
              <b-form-input
                id="tfa-confirm"
                v-model="confirmCode"
                class="field-main"
                style="max-width: 12rem; letter-spacing: 0.2em"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                placeholder="••••••"
              />
              <b-button class="akkurate-green-btn" :disabled="confirmLoading || confirmCode.replace(/\s/g, '').length !== 6" @click="confirmTotp">
                <b-spinner v-if="confirmLoading" small class="me-1" />
                Confirm
              </b-button>
            </div>
          </div>

          <!-- Recovery codes (after TOTP is active) -->
          <div v-if="status.user_two_factor_enabled" class="mt-4 pt-3 border-top">
            <h6 class="small fw-bold text-green mb-2">Recovery codes</h6>
            <p class="small text-muted">
              Store these safely. Each code works once. Regenerating invalidates previous codes.
            </p>
            <b-button size="sm" variant="outline-secondary" class="me-2 mb-2" :disabled="recoveryLoading" @click="fetchRecoveryCodes">
              <b-spinner v-if="recoveryLoading" small class="me-1" />
              {{ recoveryCodes.length ? "Refresh list" : "Show recovery codes" }}
            </b-button>
            <b-button size="sm" variant="outline-danger" class="mb-2" @click="openRegenerateModal">Regenerate codes</b-button>

            <b-alert v-if="recoveryError" show variant="danger" class="small" dismissible @dismissed="recoveryError = ''">
              {{ recoveryError }}
            </b-alert>

            <ul v-if="recoveryCodes.length" class="small font-monospace bg-white border rounded py-2 px-3 mb-0">
              <li v-for="(c, i) in recoveryCodes" :key="i">{{ c }}</li>
            </ul>

            <b-button v-if="lastConfirmedRecoveryCodes.length" size="sm" variant="link" class="p-0 mt-2" @click="showRecoveryFromConfirm = !showRecoveryFromConfirm">
              {{ showRecoveryFromConfirm ? "Hide" : "Show" }} codes from last confirmation
            </b-button>
            <ul
              v-if="showRecoveryFromConfirm && lastConfirmedRecoveryCodes.length"
              class="small font-monospace bg-warning bg-opacity-10 border border-warning rounded py-2 px-3 mt-1 mb-0"
            >
              <li v-for="(c, i) in lastConfirmedRecoveryCodes" :key="'lc-' + i">{{ c }}</li>
            </ul>
          </div>

          <div v-if="status.user_two_factor_enabled" class="mt-3">
            <b-button size="sm" variant="outline-danger" @click="openDisableModal">Disable authenticator 2FA</b-button>
          </div>
        </template>

        <template v-else>
          <p class="small text-muted mb-0">Two-factor authentication is not required by your institution at this time.</p>
        </template>
      </template>

      <b-alert v-else show variant="secondary" class="mb-0">Unable to load 2FA status.</b-alert>

      <!-- Password modal: POST .../enable -->
      <b-modal id="modal-tfa-enable" v-model="enableModalShow" title="Confirm your password" hide-footer centered>
        <p class="small">Enter your current account password to start authenticator setup (TOTP).</p>
        <b-form-input v-model="enablePassword" type="password" autocomplete="current-password" placeholder="Current password" class="mb-3" />
        <div class="d-flex justify-content-end gap-2 flex-wrap">
          <b-button variant="secondary" @click="enableModalShow = false">Cancel</b-button>
          <b-button class="akkurate-green-btn text-white" :disabled="!enablePassword || setupLoading" @click="runEnable">
            <b-spinner v-if="setupLoading" small class="me-1" />
            Generate QR
          </b-button>
        </div>
      </b-modal>

      <!-- Regenerate recovery codes -->
      <b-modal id="modal-tfa-regen" v-model="regenModalShow" title="Regenerate recovery codes" hide-footer centered>
        <p class="small">This invalidates existing recovery codes. Enter your password to continue.</p>
        <b-form-input v-model="regenPassword" type="password" autocomplete="current-password" placeholder="Current password" class="mb-3" />
        <div class="d-flex justify-content-end gap-2 flex-wrap">
          <b-button variant="secondary" @click="regenModalShow = false">Cancel</b-button>
          <b-button variant="danger" :disabled="!regenPassword || regenLoading" @click="runRegenerate">
            <b-spinner v-if="regenLoading" small class="me-1" />
            Regenerate
          </b-button>
        </div>
      </b-modal>

      <!-- Disable 2FA -->
      <b-modal id="modal-tfa-disable" v-model="disableModalShow" title="Disable authenticator 2FA" hide-footer centered>
        <p class="small">Disables TOTP for your account. Include your password if the server requires it.</p>
        <b-form-input v-model="disablePassword" type="password" autocomplete="current-password" placeholder="Password (if required)" class="mb-3" />
        <div class="d-flex justify-content-end gap-2 flex-wrap">
          <b-button variant="secondary" @click="disableModalShow = false">Cancel</b-button>
          <b-button variant="danger" :disabled="disableLoading" @click="runDisable">
            <b-spinner v-if="disableLoading" small class="me-1" />
            Disable
          </b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";

function parseSetupPayload(res) {
  const raw = res?.data?.data ?? res?.data ?? {};
  return {
    qr_code_svg: raw.qr_code_svg || null,
    setup_key: raw.setup_key || raw.secret || null,
    message: raw.message || "",
  };
}

function parseRecoveryList(res) {
  const raw = res?.data?.data ?? res?.data ?? {};
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.recovery_codes)) return raw.recovery_codes;
  if (Array.isArray(raw.codes)) return raw.codes;
  return [];
}

export default {
  name: "UserTwoFactorPanel",
  props: {
    phoneNumber: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: true,
      loadError: "",
      status: null,
      setupLoading: false,
      setupError: "",
      setupPayload: null,
      confirmCode: "",
      confirmLoading: false,
      enableModalShow: false,
      enablePassword: "",
      recoveryCodes: [],
      recoveryLoading: false,
      recoveryError: "",
      lastConfirmedRecoveryCodes: [],
      showRecoveryFromConfirm: true,
      regenModalShow: false,
      regenPassword: "",
      regenLoading: false,
      disableModalShow: false,
      disablePassword: "",
      disableLoading: false,
    };
  },
  computed: {
    institution2faOn() {
      return !!this.status?.institution_two_factor_enabled;
    },
    isSmsDelivery() {
      return this.institution2faOn && String(this.status?.institution_two_factor_delivery_method || "").toLowerCase() === "sms";
    },
    isAuthenticatorDelivery() {
      return this.institution2faOn && String(this.status?.institution_two_factor_delivery_method || "").toLowerCase() === "authenticator";
    },
    showConfirmSection() {
      if (!this.status || !this.isAuthenticatorDelivery || !this.institution2faOn) return false;
      return !!(this.setupPayload || this.status.user_pending_confirmation);
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    boolLabel(v) {
      return v ? "Yes" : "No";
    },
    async loadStatus() {
      this.loading = true;
      this.loadError = "";
      try {
        const res = await ApiService.get("users/two-factor/status");
        this.status = res.data?.data ?? res.data ?? null;
      } catch (e) {
        this.status = null;
        this.loadError = e?.response?.data?.message || "Could not load two-factor status.";
      } finally {
        this.loading = false;
      }
    },
    openEnableModal() {
      this.setupError = "";
      this.enablePassword = "";
      this.enableModalShow = true;
    },
    async runEnable() {
      if (!this.enablePassword) return;
      this.setupLoading = true;
      this.setupError = "";
      this.setupPayload = null;
      try {
        const res = await ApiService.post("users/two-factor/enable", {
          password: this.enablePassword,
        });
        this.setupPayload = parseSetupPayload(res);
        this.enableModalShow = false;
        this.enablePassword = "";
        if (window.toast?.fire && res.data?.message) {
          window.toast.fire({ icon: "info", title: res.data.message });
        }
        await this.loadStatus();
      } catch (e) {
        const st = e?.response?.status;
        const msg =
          e?.response?.data?.message ||
          (st === 403 ? "Two-factor is not available for your institution." : "") ||
          (st === 422 ? "Cannot start setup (e.g. SMS mode, already enabled, or invalid password)." : "") ||
          "Could not start authenticator setup.";
        this.setupError = msg;
      } finally {
        this.setupLoading = false;
      }
    },
    async confirmTotp() {
      const code = String(this.confirmCode || "").replace(/\s/g, "");
      if (code.length !== 6) return;
      this.confirmLoading = true;
      try {
        const res = await ApiService.post("users/two-factor/confirm", { code });
        const raw = res?.data?.data ?? res?.data ?? {};
        const codes = raw.recovery_codes;
        if (Array.isArray(codes) && codes.length) {
          this.lastConfirmedRecoveryCodes = codes;
          this.showRecoveryFromConfirm = true;
        }
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: res.data?.message || "Two-factor authentication confirmed" });
        }
        this.confirmCode = "";
        this.setupPayload = null;
        await this.loadStatus();
        if (this.status?.user_two_factor_enabled) {
          await this.fetchRecoveryCodes();
        }
      } catch (e) {
        const msg = e?.response?.data?.message || "Invalid code. Try again.";
        if (window.toast?.fire) {
          window.toast.fire({ icon: "error", title: msg });
        }
      } finally {
        this.confirmLoading = false;
      }
    },
    async fetchRecoveryCodes() {
      this.recoveryLoading = true;
      this.recoveryError = "";
      try {
        const res = await ApiService.get("users/two-factor/recovery-codes");
        this.recoveryCodes = parseRecoveryList(res);
      } catch (e) {
        this.recoveryError = e?.response?.data?.message || "Could not load recovery codes.";
        this.recoveryCodes = [];
      } finally {
        this.recoveryLoading = false;
      }
    },
    openRegenerateModal() {
      this.regenPassword = "";
      this.regenModalShow = true;
    },
    async runRegenerate() {
      if (!this.regenPassword) return;
      this.regenLoading = true;
      this.recoveryError = "";
      try {
        const res = await ApiService.post("users/two-factor/recovery-codes/regenerate", {
          password: this.regenPassword,
        });
        this.regenModalShow = false;
        this.regenPassword = "";
        const raw = res?.data?.data ?? res?.data ?? {};
        const codes = raw.recovery_codes;
        if (Array.isArray(codes)) {
          this.recoveryCodes = codes;
          this.lastConfirmedRecoveryCodes = codes;
        } else {
          await this.fetchRecoveryCodes();
        }
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: res.data?.message || "Recovery codes regenerated" });
        }
      } catch (e) {
        this.recoveryError = e?.response?.data?.message || "Could not regenerate codes.";
      } finally {
        this.regenLoading = false;
      }
    },
    openDisableModal() {
      this.disablePassword = "";
      this.disableModalShow = true;
    },
    async runDisable() {
      this.disableLoading = true;
      try {
        const body = this.disablePassword ? { password: this.disablePassword } : {};
        await ApiService.post("users/two-factor/disable", body);
        this.disableModalShow = false;
        this.disablePassword = "";
        this.setupPayload = null;
        this.recoveryCodes = [];
        this.lastConfirmedRecoveryCodes = [];
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: "Two-factor authentication disabled" });
        }
        await this.loadStatus();
      } catch (e) {
        const msg = e?.response?.data?.message || "Could not disable 2FA.";
        if (window.toast?.fire) {
          window.toast.fire({ icon: "error", title: msg });
        }
      } finally {
        this.disableLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.akkurate-tfa-note {
  border-left: 4px solid rgb(1, 134, 115);
}
/* qr_code_svg is HTML from server — constrain size for layout */
.tfa-qr-html ::v-deep svg {
  max-width: 220px;
  height: auto;
}
</style>
